import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ShowAlert } from "../../utils/showAlert";
import { UseGetStoreData } from "../getStoreData/UseGetStoreData";
import { useModal } from "../../components/organisms/modalProvider/ModalProvider";
import { PaymentLink } from "../../components/molecules/paymentLink/PaymentLink";
import { createConsultation, redispatchConsultation, consultationStatusSse, createSseConnection, urls, getDoctorById, getAllDoctors, getTicketById, getConsultationById } from "../../http";
import { sendWhatsappMessage, updateTicket, sendMeetingLinkFun, connectCallBtwDP } from "../../pages/actions/actions";
import { uploadFile } from "../../pages/actions/uploadFiles";
import { currentDateTime } from "../../utils/helper";
import { useEmergencyContact } from "../useEmergencyContact/UseEmergencyContact";
import {
  setConsultationId,
  setSseDetail,
  setAcceptedDoctorInfo,
  appendEventLog,
  resetConsultation,
  hydrateConsultation,
} from "../../redux/consultationSlice/consultationSlice";
import type { SseDetail } from "../../redux/consultationSlice/consultationSlice";
import { ticketDataSuccess } from "../../redux/ticketSlice/ticketSlice";

interface DoctorData {
  doctorId: string;
  doctorName: string;
  mobileNo: string;
  endTime: string;
  startTime: string;
  languages?: [];
  pocoDoctor?: boolean;
  doctorType?: string;
}

type ConsultationMode = "audio" | "video";
export type ConsultationStage = "requested" | "dispatched" | "timeout" | "rejected" | "accepted" | "ongoing" | "completed" | "cancelled" | "call initiated" | "call_initiated";

const TERMINAL_STATUSES = ["completed", "cancelled"];

export const useDoctorConsult = () => {
  const dispatch = useDispatch();
  const { getTicketData, getTicketStatus, officeData, getTicketUpdateData } = UseGetStoreData();
  const { showModal } = useModal();
  const { validContacts } = useEmergencyContact();
  const pocNumber = getTicketData?.poc?.mobile || getTicketUpdateData?.poc?.mobile || "";

  const consultationId = useSelector((state: any) => state.consultation.consultationId);
  const storedTicketId = useSelector((state: any) => state.consultation.ticketId);
  const rawSseDetail = useSelector((state: any) => state.consultation.sseDetail) as SseDetail | null;
  const rawAcceptedDetail = useSelector((state: any) => state.consultation.acceptedDetail) as { doctor_id: string; accepted_at: string; doctor_name?: string; doctor_phone?: string } | null;
  const rawEventLog = useSelector((state: any) => state.consultation.eventLog) as SseDetail[];

  const currentTicketId = String(getTicketData?._id ?? "");
  const sseDetail      = storedTicketId === currentTicketId ? rawSseDetail : null;
  const acceptedDetail = storedTicketId === currentTicketId ? rawAcceptedDetail : null;
  const eventLog       = storedTicketId === currentTicketId ? rawEventLog : [];

  const [consultationMode, setConsultationMode] = useState<ConsultationMode>("video");
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const [uploading, setUploading]       = useState<boolean>(false);
  const [isBtnLoading, setIsBtnLoading] = useState<boolean>(false);
  const [prescriptionDocUrl, setPrescriptionDocUrl] = useState<string>("");
  const [doctorRosters, setDoctorRosters] = useState<DoctorData[]>([]);
  const [inputPatientMobiles, setInputPatientMobiles] = useState<string[]>([getTicketData?.patient?.mobile || ""]);
  const [doctorData, setDoctorData]         = useState<DoctorData | null>(null);
  const [doctorName, setDoctorName]         = useState<string>(getTicketData?.doctorInfo?.fullName || "");
  const [doctorNumber, setDoctorNumber]     = useState<string>(getTicketData?.doctorInfo?.mobile || "");
  const [patientNumber, setPatientNumber]   = useState<string>(getTicketData?.patient?.mobile || "");
  const [inputDoctorMobiles, setInputDoctorMobiles] = useState<string[]>([]);
  const [sendMeetingLoading, setSendMeetingLoading] = useState<boolean>(false);
  const [isDoctorNumberValid, setIsDoctorNumberValid] = useState<boolean>(true);
  const [isPatientNumberValid, setIsPatientNumberValid] = useState<boolean>(true);

  useEffect(() => {
    if (getTicketData?.doctorInfo?.mobile) {
      setDoctorNumber(getTicketData.doctorInfo.mobile);
    } else if (officeData?.doctorDetails?.doctorNumber?.number) {
      setDoctorNumber((prev) => prev || String(officeData.doctorDetails.doctorNumber.number));
    }
  }, [getTicketData?.doctorInfo?.mobile, officeData?.doctorDetails?.doctorNumber?.number]);

  // Derive consultationStage directly from sseDetail
  const consultationStage = sseDetail?.status as ConsultationStage | null ?? null;

  const sseRef = useRef<{ close: () => void } | null>(null);

  const connectStatusSse = (id: string) => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    const url = consultationStatusSse(id);
    console.log("[ConsultationSSE] connecting to:", url);

    sseRef.current = createSseConnection(
      url,
      {},
      (data) => {
        console.log("[ConsultationSSE] raw event:", data);
        try {
          const parsed = JSON.parse(data) as SseDetail;
          if (parsed.status === "heartbeat") return;

          // Enrich with doctor name and timestamp for the log
          const enrichAndLog = (entry: SseDetail) => {
            const logEntry = { ...entry, logged_at: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) };
            if (entry.doctor_id) {
              getDoctorById(entry.doctor_id)
                .then((res) => {
                  dispatch(appendEventLog({ ...logEntry, doctor_name: res?.data?.data?.name?.trim() || entry.doctor_id }));
                })
                .catch(() => dispatch(appendEventLog(logEntry)));
            } else {
              dispatch(appendEventLog(logEntry));
            }
          };

          enrichAndLog(parsed);

          if (parsed.status === "accepted") {
            parsed.accepted_at = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
            if (parsed.doctor_id) {
              getDoctorById(parsed.doctor_id)
                .then((res) => {
                  const doc = res?.data?.data;
                  dispatch(setAcceptedDoctorInfo({
                    doctor_name: doc?.name?.trim() || "N/A",
                    doctor_phone: doc?.phone || "N/A",
                  }));
                })
                .catch((err) => console.error("[DoctorById] error:", err));
            }
          }

          dispatch(setSseDetail(parsed));

          if (TERMINAL_STATUSES.includes(parsed.status)) {
            console.log("[ConsultationSSE] terminal stage, closing stream");
            sseRef.current?.close();
            sseRef.current = null;
            if (parsed.status === "cancelled") {
              ShowAlert("warning", "No doctor accepted the request");
              // Fetch names for all failed doctors
              if (parsed.failed_doctors?.length) {
                Promise.all(
                  parsed.failed_doctors.map((fd: any) =>
                    getDoctorById(fd.doctor_id)
                      .then((res) => ({ ...fd, doctor_name: res?.data?.data?.name?.trim() || fd.doctor_id }))
                      .catch(() => ({ ...fd, doctor_name: fd.doctor_id }))
                  )
                ).then((enriched) => {
                  dispatch(setSseDetail({ ...parsed, failed_doctors: enriched }));
                });
              }
            }
          }
        } catch (err) {
          console.error("[ConsultationSSE] failed to parse event:", err);
        }
      },
      (err) => {
        console.error("[ConsultationSSE] stream error:", err);
        sseRef.current = null;
      }
    );
  };

  useEffect(() => {
    // Only manage the SSE connection when consultationId changes.
    // We intentionally do NOT include sseDetail?.status in the dependency array —
    // reconnecting on every status event would close and reopen the stream mid-flight,
    // causing duplicate/phantom events in the log.
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    if (consultationId && consultationId !== "external") {
      const isTerminal = sseDetail && TERMINAL_STATUSES.includes(sseDetail.status);
      if (!isTerminal) {
        console.log("[ConsultationSSE] connecting for id:", consultationId);
        connectStatusSse(consultationId);
      }
    }

    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationId]);

  // Restore consultationId from the Ticket backend on mount or when the ticket changes.
  // Fetches the full consultation + doctor request history from the database and
  // hydrates Redux so logs persist across navigation and page reloads.
  useEffect(() => {
    const ticketId = String(getTicketData?._id ?? "");
    if (!ticketId) return;
    // Already hydrated for this ticket — skip
    if (storedTicketId === ticketId && consultationId) return;

    getTicketById(ticketId)
      .then(async (res: any) => {
        const cid = res?.data?.data?.consultation_id ?? res?.data?.consultation_id;
        if (!cid) {
          const docInfo = res?.data?.data?.doctorInfo || getTicketData?.doctorInfo;
          if (docInfo?.fullName === "External Doctor" || (docInfo?.mobile && docInfo?.fullName)) {
            const isPrescriptionSent = (docInfo?.clinicalNotes && docInfo.clinicalNotes.length > 0);
            const loggedAt = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
            const restoredEventLog: SseDetail[] = [
              { status: "call initiated", doctor_name: docInfo.fullName || "External Doctor", logged_at: loggedAt }
            ];
            if (isPrescriptionSent) {
              restoredEventLog.push({ status: "completed", doctor_name: docInfo.fullName || "External Doctor", logged_at: loggedAt });
            }
            dispatch(
              hydrateConsultation({
                ticketId,
                consultationId: "external",
                sseDetail: { status: isPrescriptionSent ? "completed" : "call initiated", room_id: "external" },
                acceptedDetail: {
                  doctor_id: "external",
                  accepted_at: loggedAt,
                  doctor_name: docInfo.fullName || "External Doctor",
                  doctor_phone: docInfo.mobile || "",
                },
                eventLog: restoredEventLog,
              })
            );
            return;
          }
          dispatch(resetConsultation());
          return;
        }

        console.log("[ConsultationRestore] restoring consultationId from ticket:", cid);

        try {
          const consultRes = await getConsultationById(String(cid));
          const responseData = consultRes?.data?.data;
          // Support both new format { ...consultation, doctor_requests, events } and legacy format
          const consultation = responseData?.consultation || responseData;
          const doctorRequests: any[] = responseData?.doctor_requests || [];
          const dbEvents: any[] = consultation?.events || [];

          if (!consultation) {
            dispatch(setConsultationId({ ticketId, consultationId: String(cid) }));
            return;
          }

          // ── Build sseDetail from consultation document ────────────────────
          const restoredSseDetail: SseDetail = {
            status: consultation.status,
            room_id: consultation.room_id,
            call_duration_seconds: consultation.call_duration_seconds,
          };

          // ── Build acceptedDetail ──────────────────────────────────────────
          let restoredAcceptedDetail: {
            doctor_id: string;
            accepted_at: string;
            doctor_name?: string;
            doctor_phone?: string;
          } | null = null;

          // Helper: format a date to IST time string
          const toTimeStr = (d: string | Date) =>
            new Date(d).toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            });

          // ── Build eventLog: prefer persisted events[] from DB ─────────────
          let restoredEventLog: SseDetail[] = [];

          if (dbEvents.length > 0) {
            // ── PRIMARY PATH: use the events stored in the consultation doc ──
            //    These were recorded by emitStatusUpdate on the backend.
            //    Doctor names are already resolved and stored in the event.

            // Resolve accepted doctor info for the acceptedDetail card
            if (consultation.doctor_id && consultation.accepted_at) {
              const docId = String(consultation.doctor_id);
              const acceptedEvt = dbEvents.find(
                (e: any) => e.status === "accepted" && String(e.doctor_id) === docId
              );
              // Try to get phone from a single API call for the accepted doctor
              let docPhone = "N/A";
              try {
                const docRes: any = await getDoctorById(docId);
                const doc = docRes?.data?.data;
                docPhone = doc?.phone || "N/A";
                // Also use the API name if the event didn't have one
                if (!acceptedEvt?.doctor_name && doc?.name) {
                  restoredAcceptedDetail = {
                    doctor_id: docId,
                    accepted_at: toTimeStr(consultation.accepted_at),
                    doctor_name: doc.name.trim(),
                    doctor_phone: docPhone,
                  };
                }
              } catch { /* ignore */ }

              if (!restoredAcceptedDetail) {
                restoredAcceptedDetail = {
                  doctor_id: docId,
                  accepted_at: toTimeStr(consultation.accepted_at),
                  doctor_name: acceptedEvt?.doctor_name || "Doctor",
                  doctor_phone: docPhone,
                };
              }
            }

            // Map DB events to SseDetail shape the UI expects
            restoredEventLog = dbEvents.map((evt: any) => {
              const entry: SseDetail = {
                status: evt.status,
                logged_at: evt.timestamp ? toTimeStr(evt.timestamp) : undefined,
              };
              if (evt.doctor_id) entry.doctor_id = String(evt.doctor_id);
              if (evt.doctor_name) entry.doctor_name = evt.doctor_name;
              if (evt.doctor_index != null) entry.doctor_index = evt.doctor_index;
              if (evt.total_doctors != null) entry.total_doctors = evt.total_doctors;
              if (evt.call_duration_seconds != null)
                entry.call_duration_seconds = evt.call_duration_seconds;
              if (evt.extra?.failed_doctors) entry.failed_doctors = evt.extra.failed_doctors;
              return entry;
            });
          } else {
            // ── FALLBACK: reconstruct from doctor_requests (old consultations) ──
            //    This path is for consultations created before the events[] feature.

            // Resolve doctor names in parallel
            const allDoctorIds = new Set<string>(doctorRequests.map((r: any) => String(r.doctor_id)));
            if (consultation.doctor_id) allDoctorIds.add(String(consultation.doctor_id));
            const uniqueDoctorIds = [...allDoctorIds];

            const doctorInfoMap: Record<string, { name: string; phone: string }> = {};
            await Promise.allSettled(
              uniqueDoctorIds.map((did: string) =>
                getDoctorById(did)
                  .then((docRes: any) => {
                    const doc = docRes?.data?.data;
                    doctorInfoMap[did] = {
                      name: doc?.name?.trim() || "Doctor",
                      phone: doc?.phone || "N/A",
                    };
                  })
              )
            );

            if (consultation.doctor_id && consultation.accepted_at) {
              const docId = String(consultation.doctor_id);
              restoredAcceptedDetail = {
                doctor_id: docId,
                accepted_at: toTimeStr(consultation.accepted_at),
                doctor_name: doctorInfoMap[docId]?.name || "N/A",
                doctor_phone: doctorInfoMap[docId]?.phone || "N/A",
              };
            }

            const totalDoctors = consultation.doctor_ids?.length || doctorRequests.length;

            doctorRequests.forEach((req: any, idx: number) => {
              const docId = String(req.doctor_id);
              const docName = doctorInfoMap[docId]?.name || docId;

              // "dispatched" entry
              restoredEventLog.push({
                status: "dispatched",
                doctor_id: docId,
                doctor_name: docName,
                doctor_index: idx + 1,
                total_doctors: totalDoctors,
                logged_at: toTimeStr(req.requested_at),
              });

              // Doctor response entry (accepted / rejected / timeout)
              if (req.status !== "requested" && req.responded_at) {
                restoredEventLog.push({
                  status: req.status,
                  doctor_id: docId,
                  doctor_name: docName,
                  doctor_index: idx + 1,
                  total_doctors: totalDoctors,
                  logged_at: toTimeStr(req.responded_at),
                });
              }
            });

            // Terminal status entry (completed / cancelled)
            if (["completed", "cancelled"].includes(consultation.status)) {
              const terminalTime = consultation.ended_at || consultation.updatedAt;
              const terminalEntry: SseDetail = {
                status: consultation.status,
                call_duration_seconds: consultation.call_duration_seconds,
              };
              if (terminalTime) {
                terminalEntry.logged_at = toTimeStr(terminalTime);
              }
              if (consultation.status === "cancelled") {
                terminalEntry.failed_doctors = doctorRequests
                  .filter((r: any) => ["timeout", "rejected"].includes(r.status))
                  .map((r: any) => ({
                    doctor_id: String(r.doctor_id),
                    reason: r.status,
                    doctor_name: doctorInfoMap[String(r.doctor_id)]?.name || String(r.doctor_id),
                  }));
              }
              restoredEventLog.push(terminalEntry);
            }
          }

          // ── Hydrate Redux atomically ──────────────────────────────────────
          dispatch(
            hydrateConsultation({
              ticketId,
              consultationId: String(cid),
              sseDetail: restoredSseDetail,
              acceptedDetail: restoredAcceptedDetail,
              eventLog: restoredEventLog,
            })
          );
        } catch (err) {
          console.error("[ConsultationRestore] failed to fetch consultation details:", err);
          // Fallback: just set the consultation ID (old behavior)
          dispatch(setConsultationId({ ticketId, consultationId: String(cid) }));
        }
      })
      .catch(() => {
        dispatch(resetConsultation());
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTicketData?._id]);

  const handleDoctorChange = (doctorId: string) => {
    const found = doctorRosters.find((d) => d.doctorId === doctorId) || null;
    setDoctorData(found);
    setDoctorName(found?.doctorName || "");
    setDoctorNumber(found?.mobileNo || "");
    setInputDoctorMobiles(found ? [found.mobileNo] : []);
    setIsDoctorNumberValid(true);
  };

  const handleDoctorNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!doctorData) setDoctorName(e.target.value);
  };

  const handleDoctorMobileNo = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!doctorData) {
      const digits = e.target.value.replace(/\D/g, "");
      const chunks: string[] = [];
      for (let i = 0; i < digits.length; i += 10) chunks.push(digits.slice(i, i + 10));
      setDoctorNumber(chunks.join(","));
      setIsDoctorNumberValid(!chunks.some((n) => !/^[0-9]{10}$/.test(n)));
      setInputDoctorMobiles(chunks.filter((n) => /^[0-9]{10}$/.test(n)));
    }
  };

  const handlePatientMobileNo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "");
    const chunks: string[] = [];
    for (let i = 0; i < digits.length; i += 10) chunks.push(digits.slice(i, i + 10));
    setPatientNumber(chunks.join(","));
    setIsPatientNumberValid(!chunks.some((n) => !/^[0-9]{10}$/.test(n)));
    setInputPatientMobiles(chunks.filter((n) => /^[0-9]{10}$/.test(n)));
  };

  const isSendMeetingLinkDisabled = () =>
    getTicketData?.ticketStatus === "New" ||
    getTicketData?.ticketStatus === "Completed" ||
    sendMeetingLoading ||
    !isDoctorNumberValid ||
    !isPatientNumberValid;

  const sendMeetingLink = async () => {
    setSendMeetingLoading(true);
    if (!doctorData && inputDoctorMobiles.length <= 0) {
      alert("Please select a doctor or add custom doctor numbers");
      setSendMeetingLoading(false);
      return;
    }
    if (inputPatientMobiles.length <= 0) {
      alert("Please add patient numbers");
      setSendMeetingLoading(false);
      return;
    }
    const uniqueNumbers = new Set<string>();
    const mobileNumbers: { number: string; role: "patient" | "doctor" }[] = [];
    const add = (num: string | undefined, role: "patient" | "doctor") => {
      if (num && !uniqueNumbers.has(num)) { mobileNumbers.push({ number: num, role }); uniqueNumbers.add(num); }
    };
    add(getTicketData?.patient?.mobile, "patient");
    add(getTicketData?.ticketRaisedBy?.mobile, "patient");
    if (getTicketData?.patient?.whatsappNumber !== getTicketData?.patient?.mobile)
      add(getTicketData?.patient?.whatsappNumber, "patient");
    inputPatientMobiles.forEach((n) => add(n, "patient"));
    add(doctorData?.mobileNo, "doctor");
    inputDoctorMobiles.forEach((n) => add(n, "doctor"));

    const payload = {
      beneficiaryId: getTicketData?.patient?.id,
      uploadPrescriptionUrl: `${urls.AZURE_URI}/signedUrl/generateSignedUrl`,
      medicalInfoUrl: `${urls.BENEFICIARY_URI}/beneficiary/medical-info-perk/${getTicketData?.patient?.id}`,
      mobileNumbers,
    };
    const response = await sendMeetingLinkFun(payload);
    setSendMeetingLoading(false);
    if (response) {
      await updateTicket(String(getTicketData._id), {
        notes: [...getTicketData.notes, { date: currentDateTime(), message: "Meeting link shared to patient" }],
        doctorInfo: {
          fullName: doctorName,
          mobile: doctorNumber,
          source: doctorData ? "poco" : "external",
          clinicalNotes: [...(getTicketData?.doctorInfo?.clinicalNotes ?? [])],
          agentNotes: [...(getTicketData?.doctorInfo?.agentNotes ?? [])],
        },
      });
      ShowAlert("success", "Meeting Link Sent Successfully");
    }
  };

  const HandleDoctorRoasters = async () => {
    try {
      const res = await getAllDoctors();
      const doctors: DoctorData[] = (res?.data?.data || [])
        .filter((d: any) => d.doctor_type === "pococare")
        .map((d: any) => ({
          doctorId: d._id,
          doctorName: d.name?.trim() || "N/A",
          mobileNo: d.phone || "",
          startTime: d.time_slots?.startTime || "",
          endTime: d.time_slots?.endTime || "",
          doctorType: d.doctor_type || "",
        }));
      setDoctorRosters(doctors);
    } catch (e) {
      console.error("[HandleDoctorRoasters] error:", e);
    }
  };

  const sendConsultationRequest = async () => {
    if (!getTicketData?._id) {
      ShowAlert("warning", "No active ticket found");
      return;
    }
    setIsRequestLoading(true);

    // ── EXTERNAL / MANUAL DOCTOR FLOW ──────────────────────────────────────
    if (!doctorData) {
      if (!doctorNumber?.trim() || !isDoctorNumberValid) {
        ShowAlert("warning", "Please provide a valid doctor phone number");
        setIsRequestLoading(false);
        return;
      }

      // Audio mode — direct Twilio DP call
      if (consultationMode === "audio") {
        try {
          const patientMobile = getTicketData?.patient?.mobile;
          const doctorMobile = doctorNumber;
          await connectCallBtwDP({ agentId: doctorMobile, customerNumber: patientMobile }, String(getTicketData._id));
          ShowAlert("success", "Audio call initiated between doctor and patient");
        } catch (e: any) {
          ShowAlert("error", e?.response?.data?.message || "Failed to initiate call");
        } finally {
          setIsRequestLoading(false);
        }
        return;
      }

      // Video mode — direct meeting link via WhatsApp
      try {
        const uniqueNumbers = new Set<string>();
        const mobileNumbers: { number: string; role: "patient" | "doctor" }[] = [];
        const add = (num: string | undefined, role: "patient" | "doctor") => {
          if (num && !uniqueNumbers.has(num)) {
            mobileNumbers.push({ number: num, role });
            uniqueNumbers.add(num);
          }
        };
        add(getTicketData?.patient?.mobile, "patient");
        add(getTicketData?.ticketRaisedBy?.mobile, "patient");
        if (getTicketData?.patient?.whatsappNumber !== getTicketData?.patient?.mobile) {
          add(getTicketData?.patient?.whatsappNumber, "patient");
        }
        add(doctorNumber, "doctor");

        const payload = {
          beneficiaryId: getTicketData?.patient?.id,
          uploadPrescriptionUrl: `${urls.AZURE_URI}/signedUrl/generateSignedUrl`,
          medicalInfoUrl: `${urls.BENEFICIARY_URI}/beneficiary/medical-info-perk/${getTicketData?.patient?.id}`,
          mobileNumbers,
        };

        const response = await sendMeetingLinkFun(payload);
        if (response) {
          await updateTicket(String(getTicketData._id), {
            notes: [
              ...getTicketData.notes,
              { date: currentDateTime(), message: `Meeting link shared to patient and Doctor (${doctorNumber})` },
            ],
            doctorInfo: {
              fullName: "External Doctor",
              mobile: doctorNumber,
              source: "external",
              clinicalNotes: [...(getTicketData?.doctorInfo?.clinicalNotes ?? [])],
              agentNotes: [...(getTicketData?.doctorInfo?.agentNotes ?? [])],
            },
          });

          // Hydrate event timeline for External Doctor
          const loggedAt = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
          dispatch(
            hydrateConsultation({
              ticketId: String(getTicketData._id),
              consultationId: "external",
              sseDetail: { status: "call initiated", room_id: "external" },
              acceptedDetail: {
                doctor_id: "external",
                accepted_at: loggedAt,
                doctor_name: "External Doctor",
                doctor_phone: doctorNumber,
              },
              eventLog: [
                { status: "call initiated", doctor_name: "External Doctor", logged_at: loggedAt },
              ],
            })
          );

          ShowAlert("success", "Meeting link shared successfully via WhatsApp");
        }
      } catch (e: any) {
        ShowAlert("error", e?.message || "Failed to send meeting link");
      } finally {
        setIsRequestLoading(false);
      }
      return;
    }

    // ── INTERNAL POCOCARE DOCTOR SSE FLOW ───────────────────────────────────
    if (consultationMode === "audio") {
      try {
        const patientMobile = getTicketData?.patient?.mobile;
        const doctorMobile = doctorData.mobileNo;
        await connectCallBtwDP({ agentId: doctorMobile, customerNumber: patientMobile }, String(getTicketData._id));
        // Persist Poco doctor identity on the ticket so prescription upload/send
        // can distinguish this from an external-doctor consultation.
        await updateTicket(String(getTicketData._id), {
          doctorInfo: {
            fullName: doctorData.doctorName || doctorName || "",
            mobile: String(doctorData.mobileNo || "").replace(/\D/g, "").slice(-10),
            source: "poco",
            clinicalNotes: [...(getTicketData?.doctorInfo?.clinicalNotes ?? [])],
            agentNotes: [...(getTicketData?.doctorInfo?.agentNotes ?? [])],
          },
        });
        ShowAlert("success", "Audio call initiated between doctor and patient");
      } catch (e: any) {
        ShowAlert("error", e?.response?.data?.message || "Failed to initiate call");
      } finally {
        setIsRequestLoading(false);
      }
      return;
    }

    // Video mode — SSE consultation flow
    const currentTicketId = String(getTicketData?._id ?? "");
    const existingConsultationId = storedTicketId === currentTicketId ? consultationId : null;
    try {
      let id: string;

      if (existingConsultationId) {
        await redispatchConsultation(existingConsultationId, [doctorData.doctorId]);
        id = existingConsultationId;
        console.log("[ConsultationSSE] redispatched existing consultation:", id);
      } else {
        const res = await createConsultation({
          ticket_id: getTicketData._id,
          consultation_mode: consultationMode,
          doctor_ids: [doctorData.doctorId],
        });
        id = res?.data?.data?.consultation_id || res?.data?.data?._id || res?.data?.data?.id;
        console.log("[ConsultationSSE] API response:", res?.data);
        console.log("[ConsultationSSE] consultation_id:", id);
      }

      const loggedAt = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      dispatch(
        appendEventLog({
          status: "dispatched",
          doctor_id: doctorData.doctorId,
          doctor_name: doctorName,
          logged_at: loggedAt,
        })
      );

      if (id) {
        dispatch(setConsultationId({ ticketId: String(getTicketData._id), consultationId: String(id) }));
      }
      dispatch(setSseDetail({ status: "requested" }));
      if (id) {
        connectStatusSse(String(id));
      }
      ShowAlert("success", "Video consultation request sent");
    } catch (e: any) {
      ShowAlert("error", e?.response?.data?.message || "Failed to send consultation request");
    } finally {
      setIsRequestLoading(false);
    }
  };

  const handlePrescriptionDoc = async (files: any) => {
    const benId = getTicketData?.patient?.id || getTicketData?.ticketRaisedBy?.benId;
    if (!getTicketData?._id || !benId) return;
    setUploading(true);
    try {
      const res = await uploadFile(benId, files, "doctorPrescription");
      setUploading(false);
      localStorage.setItem("token", res.token);
      const response = await updateTicket(String(getTicketData._id), {
        doctorInfo: {
          ...getTicketData.doctorInfo,
          // Explicitly persist doctor identity so the backend can read it
          // when creating consultation/prescription records on Send.
          // For the audio/on-premises flow, mobile is only in local state
          // (never written to the ticket by the consultation request call).
          fullName: getTicketData.doctorInfo?.fullName || doctorData?.doctorName || doctorName || "External Doctor",
          mobile: getTicketData.doctorInfo?.mobile || doctorNumber || "",
          source: getTicketData.doctorInfo?.source || (doctorData ? "poco" : "external"),
          clinicalNotes: [
            ...(getTicketData?.doctorInfo?.clinicalNotes || []),
            { url: res.url, type: "img", tags: ["check"] },
          ],
        },
      });
      dispatch(ticketDataSuccess(response?.data));
      setPrescriptionDocUrl(`${urls.AZURE_URI}/signedUrl/get-pdf/.pdf`);
    } catch (e: any) {
      setUploading(false);
      ShowAlert("error", e?.response?.data?.message || "Upload failed");
    }
  };

  const handleViewCurrentDocument = () => {
    const token = localStorage.getItem("token");
    const url = prescriptionDocUrl || getTicketData?.doctorInfo?.clinicalNotes?.[0]?.url || "";
    if (url) window.open(`${url}?token=${token}`, "_blank");
  };

  const sendWhatsAppMessageToEm = async (payload: any) => {
    try {
      await sendWhatsappMessage(payload);
    } catch (e: any) {
      ShowAlert("error", e?.response?.data?.message || "Failed to send WhatsApp message");
    }
  };

  const handlePrescriptionSend = async () => {
    if (!getTicketData?._id) return;
    setIsBtnLoading(true);
    try {
      const token = localStorage.getItem("token");
      const url = prescriptionDocUrl || `${urls.AZURE_URI}/signedUrl/get-pdf/.pdf`;
      const beneficiaryName = getTicketData?.patient?.fullName || getTicketData?.ticketRaisedBy?.fullName || "N/A";

      await updateTicket(String(getTicketData._id), {
        notes: [
          ...getTicketData.notes,
          { date: currentDateTime(), message: "Doctor prescription shared to Patient" },
        ],
      });

      await sendWhatsAppMessageToEm({
        template: "NOTIFY_EC_ON_DOCTOR_CONSULT",
        data: { to: [...validContacts], beneficiary: beneficiaryName },
      });

      await sendWhatsAppMessageToEm({
        template: "NOTIFY_PATIENT_ON_PRESCRIPTION",
        data: { to: [...validContacts], url: `${url}?token=${token}` },
      });

      // Update consultation log & status to completed
      const loggedAt = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      const currentDoctorName = acceptedDetail?.doctor_name || (consultationId === "external" ? "External Doctor" : undefined);
      dispatch(
        appendEventLog({
          status: "completed",
          doctor_name: currentDoctorName,
          logged_at: loggedAt,
        })
      );
      dispatch(
        setSseDetail({
          ...(sseDetail || {}),
          status: "completed",
        })
      );

      ShowAlert("success", "Prescription sent successfully");
    } catch (e: any) {
      ShowAlert("error", e?.response?.data?.message || "Failed to send prescription");
    } finally {
      setIsBtnLoading(false);
    }
  };

  const sendDoctorConsultPaymentLink = () => {
    showModal(<PaymentLink component="doctor-consultation" />, "Doctor-Consultation");
  };

  const isSendRequestDisabled =
    getTicketStatus === "completed" ||
    getTicketStatus === "new" ||
    (!doctorData && (!doctorNumber?.trim() || !isDoctorNumberValid));

  return {
    consultationMode,
    setConsultationMode,
    isRequestLoading,
    consultationStage,
    sseDetail,
    acceptedDetail,
    eventLog,
    sendConsultationRequest,
    sendDoctorConsultPaymentLink,
    getTicketStatus,
    getTicketData,
    uploading,
    isBtnLoading,
    handlePrescriptionDoc,
    handleViewCurrentDocument,
    handlePrescriptionSend,
    doctorRosters,
    inputPatientMobiles,
    handleDoctorChange,
    HandleDoctorRoasters,
    doctorData,
    doctorName,
    doctorNumber,
    patientNumber,
    pocNumber,
    handleDoctorNameChange,
    handleDoctorMobileNo,
    handlePatientMobileNo,
    isDoctorNumberValid,
    isPatientNumberValid,
    isSendMeetingLinkDisabled,
    sendMeetingLink,
    sendMeetingLoading,
    isSendRequestDisabled,
  };
};
